import { useEffect, useMemo, useRef, useState } from 'react'
import { MdAdd, MdArticle, MdAutoAwesomeMosaic, MdCheck, MdClose, MdEdit, MdMenu } from 'react-icons/md'
import cn from 'clsx'

import * as actions from '@actions'
import * as store from '@store'
import RunsheetTabRemoveModal from './RunsheetTabRemoveModal'
import { useWireValue } from '@forminator/react-wire'
import { toast } from 'react-toastify'
import { TAB_TYPE_DEED, TAB_TYPE_LEASE } from '@/constants'
import { checkIfNameExists } from '@/components/shared/utils'
import useBetterSearchParams from '@hook/useBetterSearchParams'
import { titleCase } from '@/utils'

const RunsheetTab = ({ runsheetId, canEdit = true, shouldSelectTab = () => true }) => {
    
    const { searchParams, updateSearchParams } = useBetterSearchParams()
    const runsheet = useWireValue(store.currentRunsheet)
    const tabs = useWireValue(store.runsheetTabs)
    const runsheetChains = useWireValue(store.runsheetChains)
    const runsheetTracts = useWireValue(store.runsheetTracts)
    const activeTab = useMemo(() => searchParams.get('activeTab'), [searchParams])
    
    const [deleteTabShow, setDeleteTabShow] = useState(false)
    const [editing, setEditing] = useState(false)
    const tabListRef = useRef(null)
    
    const selectTab = tabId => {
        
        if (tabId === activeTab) return
        
        if (!shouldSelectTab(tabId)) return
        
        updateSearchParams({
            activeTab: tabId,
        })
        
    }
    
    const addTab = async () => {
        const lastTab = tabs[tabs.length - 1]
        const newTab = {
            name: checkIfNameExists(tabs, 'RunsheetTab'),
            tabOrder: lastTab.tabOrder + 1,
        }
        const savedTab = await actions.createRunsheetTab(runsheetId, newTab)
        
        // toast.success(`Successfully created tab "${savedTab.name}".`)
        selectTab(savedTab.id)
        setEditing(true)
        setTimeout(() => {
            const el = tabListRef.current.querySelector('.tab.tab-active div')
            
            el.focus()
            window.getSelection().selectAllChildren(el)
        }, 100)
    }
    
    const handleDblClickTab = tab => {
        if (!canEdit) return
        
        if (tab.id !== '0') {
            setEditing(true)
            const el = tabListRef.current.querySelector(`#tab-${tab.id}`)
            
            el.focus()
            window.getSelection().selectAllChildren(el)
        }
    }
    
    const renameTab = async ev => {
        setEditing(false)
        
        const el = tabListRef.current.querySelector(`#tab-${activeTab}`)
        
        el?.blur()
        
        if (ev.target.textContent === ev.target.dataset.originalName) return
        
        if (!ev.target.textContent.length) return ev.target.textContent = ev.target.dataset.originalName
        
        await actions.updateRunsheetTab(activeTab, { name: checkIfNameExists(tabs, ev.target.textContent) })
    }
    
    const keyDownTab = async ev => {
        ev.stopPropagation()
        
        if (ev.key === 'Escape') {
            ev.target.textContent = ev.target.dataset.originalName
            setEditing(false)
        } else if (ev.key === 'Tab' || ev.key === 'Enter')
            await renameTab(ev)
        
        if (ev.target.textContent.length > 50 && ev.which >= 48 || ev.key === 'Enter')
            return ev.preventDefault()
        
    }
    
    const blurTab = async ev => {
        setEditing(false)
        
        if (ev.target.textContent !== ev.target.dataset.originalName)
            await renameTab(ev)
    }
    
    const removeTab = async tab => {
        
        try {
            await actions.deleteRunsheetTab(tab.id)
            selectTab(0)
            toast.success(`Deleted tab "${tab.name}" successfully.`)
        } catch (err) {
            toast.error('Failed to delete tab.')
        }
        
    }
    
    const selectTabType = async (tab, newType) => {
        if (tab.type === newType) return
        
        const hasChainedDocs =
            (newType === TAB_TYPE_LEASE && runsheetChains?.titleBreakdowns?.some(tb => tb.runsheetTabId === tab.id)) ||
            (newType === TAB_TYPE_DEED && runsheetChains?.leaseholdChains?.some(tb => tb.runsheetTabId === tab.id))
        
        if (hasChainedDocs)
            return toast.error(
                `Can't change Tab type to "${titleCase(newType)}". Tab "${tab.name}" has already chained documents.`)
        
        const assignedTract = runsheetTracts.find(rt =>
            rt.joins.some(j => j.runsheetTabId === tab.id))
        
        let join, rsName, tabName, msg
        
        // Maybe this is unnecessary, bc we not allow user to assign a tract to more than one tab
        if (assignedTract) {
            
            join = assignedTract.joins.find(
                j => j.runsheetTabId !== tab.id && j.ownershipType === TAB_TYPE_DEED)
            
            rsName = join && join.runsheetId === runsheet.id ? runsheet.name : undefined
            tabName = join && tabs.find(t => t.id === join.rusheetTabId)?.name
            
            if (tabName)
                msg = `The Tab "${tabName}" is associated as Deed for Tract "${assignedTract.name}".`
            else if (rsName)
                msg = `The Main Tab from Runsheet "${rsName}" is associated as Deed for Tract "${assignedTract.name}".`
            else if (join)
                msg = `There's already a Tab or Runsheet associated as Deed for Tract "${assignedTract.name}".`
            
        }
        
        if (newType === TAB_TYPE_DEED && join)
            return toast.error(`Can't change Tab type to Deed. ${msg}`)
        
        try {
            await actions.updateRunsheetTab(tab.id, { type: newType })
        } catch (err) {
            toast.error('Failed to change Tab type')
        }
        
    }
    
    useEffect(() => {
        if (!tabs?.some(t => t.id === activeTab))
            selectTab(0)
    }, [activeTab, tabs])
    
    return (
        <>
            <div className="flex bg-base-200">
                {canEdit && <div className="bg-base-200 px-2 py-1">
                    <a role="tab" className="tab px-2 bg-base-100 rounded"
                        onClick={() => addTab()}><MdAdd /></a>
                </div>}
                <div ref={tabListRef} role="tablist"
                    className="px-2 tabs tabs-boxed justify-start rounded-none gap-1
                        max-w-[calc(100vw-48px)]">
                    {tabs.map(tab => <div
                        role="tab"
                        key={tab.id}
                        onClick={() => selectTab(tab.id) }
                        className={cn('tab px-2', { 'tab-active': (!activeTab && !tab.id) || tab.id === activeTab })}>
                        {tab.type === TAB_TYPE_LEASE && <MdArticle className="text-xl mr-2" />}
                        {tab.type === TAB_TYPE_DEED && <MdAutoAwesomeMosaic className="text-xl mr-2" />}
                        
                        <div
                            className="px-1 !outline-none focus:ring-2 ring-base-300 rounded"
                            id={`tab-${tab.id}`}
                            data-original-name={tab.name}
                            onDoubleClick={() => handleDblClickTab(tab)}
                            onKeyDown={keyDownTab}
                            onBlur={blurTab}
                            tabIndex={0}
                            suppressContentEditableWarning
                            contentEditable={tab.id === activeTab && editing}>
                            {tab.name}
                        </div>
                        
                        {canEdit && tab.id !== '0' && tab.id === activeTab &&
                            <div className="flex dropdown dropdown-top dropdown-end">
                                <div tabIndex="0" role="button"
                                    className="ml-1 !p-1 btn btn-ghost btn-sm rounded-full !min-h-fit">
                                    <MdMenu className="text-lg" />
                                </div>
                                <ul tabIndex="0"
                                    className="dropdown-content menu runsheet-tab-dropdown">
                                    
                                    <li>
                                        <div className="cursor-pointer"
                                            onClick={() => selectTabType(tab, TAB_TYPE_DEED)}>
                                            <MdAutoAwesomeMosaic className="text-lg" />
                                            Type: Deed
                                            <MdCheck className={cn({ 'invisible': tab.type !== TAB_TYPE_DEED })}/>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="cursor-pointer"
                                            onClick={() => selectTabType(tab, TAB_TYPE_LEASE)}>
                                            <MdArticle className="text-lg" />
                                            Type: Lease
                                            <MdCheck className={cn({ 'invisible': tab.type !== TAB_TYPE_LEASE })} />
                                        </div>
                                    </li>
                                    
                                    <div className="divider my-1"></div>
                                    
                                    <li><a onClick={ev => {
                                        const inputEl = ev.target.ownerDocument.querySelector(`#tab-${tab.id}`)
                                        
                                        handleDblClickTab(tab)(inputEl)
                                    }}><MdEdit />Rename</a></li>
                                    <li><a className="text-error" onClick={e => {
                                        e.stopPropagation()
                                        setDeleteTabShow(true)
                                    }}><MdClose /> Delete</a></li>
                                </ul>
                            </div>
                        }
                    </div>,
                    )}
                </div>
            </div>
            
            <RunsheetTabRemoveModal
                show={deleteTabShow}
                tabs={tabs}
                tabId={activeTab}
                onClose={() => setDeleteTabShow(false)}
                onDelete={removeTab}/>
        </>
    )
}

export default RunsheetTab
